/**
 * Library items by id for names and thumbnails outside the Content page. Cards that ask in the same tick share one
 * request, and answers are kept for a minute (the whole library is never downloaded for this).
 */
import type { Layout, MediaAssetRecord, Playlist } from '@codot/contracts';
import { mediaApi } from '../api/content.api';

const TTL_MS = 60_000;
const cache = new Map<string, { at: number; p: Promise<MediaAssetRecord | null> }>();
let pending = new Map<string, (v: MediaAssetRecord | null) => void>();

function flush(): void {
  const batch = pending;
  pending = new Map();
  const ids = [...batch.keys()];
  mediaApi.byIds(ids).then(
    (list) => {
      const byId = new Map(list.map((a) => [a.id, a]));
      for (const id of ids) batch.get(id)!(byId.get(id) ?? null);
    },
    () => {
      for (const id of ids) {
        cache.delete(id); // try again next time
        batch.get(id)!(null);
      }
    },
  );
}

/** The items with these ids that exist and you may see. */
export async function lookupMedia(ids: readonly string[]): Promise<Map<string, MediaAssetRecord>> {
  const now = Date.now();
  const promises = [...new Set(ids)].map((id) => {
    const hit = cache.get(id);
    if (hit && now - hit.at < TTL_MS) return hit.p;
    if (!pending.size) queueMicrotask(flush);
    const p = new Promise<MediaAssetRecord | null>((resolve) => pending.set(id, resolve));
    cache.set(id, { at: now, p });
    return p;
  });
  const found = await Promise.all(promises);
  return new Map(found.flatMap((a) => (a ? [[a.id, a] as const] : [])));
}

/** Library items these placements show: media directly, and what their campaigns and playlists hold. */
export function contentMediaIds(
  refs: readonly { kind: string; id: string }[],
  campaigns: ReadonlyMap<string, Layout>,
  playlists: ReadonlyMap<string, Playlist>,
): string[] {
  const ids = new Set<string>();
  const campaign = (id: string) => campaigns.get(id)?.zones.forEach((z) => z.items.forEach((i) => ids.add(i.assetId)));
  for (const r of refs) {
    if (r.kind === 'media') ids.add(r.id);
    else if (r.kind === 'campaign') campaign(r.id);
    else if (r.kind === 'playlist')
      for (const it of playlists.get(r.id)?.items ?? []) it.content.kind === 'media' ? ids.add(it.content.id) : campaign(it.content.id);
  }
  return [...ids];
}
